import { gql } from '@apollo/client';
import {
  AlertDescription,
  AlertTitle,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { TableInstance } from 'react-table';
import { InfoAlert } from '~components/ui/Alert';
import { ChevronDownIcon, ChevronUpIcon } from '~components/ui/icons';
import { useFeatureFlag } from '~utils/features';
import { isWaveSubscription } from '~utils/subscriptions';
import { UserCustomersTable_CustomerFragment as CustomerFragment } from './__generated__/UserCustomersTable.graphql';
import { UserCustomersTableFilter } from './Filtering';
import { UserCustomersTablePageSizeSelector, UserCustomersTablePagination } from './Pagination';

interface UserCustomersTableProps {
  table: TableInstance<CustomerFragment>;
}

export function UserCustomersTable({ table }: UserCustomersTableProps) {
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } = table;

  const { isEnabled: isSubscriptionsEnabled } = useFeatureFlag('subscriptions');

  return (
    <VStack spacing="6" alignItems="stretch">
      <Text color="gray.500">Select customers in this table to add them to this user.</Text>
      {isSubscriptionsEnabled && (
        <InfoAlert mt="6" mb="10">
          <AlertTitle>
            Customer management is only available for customers connected to an Essential plan
          </AlertTitle>
          <AlertDescription>
            Connect your customer to an Essential plan via the subscriptions section on the customer
            edit page or the subscriptions settings page.
          </AlertDescription>
        </InfoAlert>
      )}
      <UserCustomersTableFilter table={table} />
      <Table {...getTableProps()} variant="simple" width="100%" marginTop="8">
        <Thead>
          {headerGroups.map((headerGroup) => {
            const headerGroupProps = headerGroup.getHeaderGroupProps();

            return (
              <Tr {...headerGroupProps} key={headerGroupProps.key}>
                {headerGroup.headers.map((column) => {
                  const sortProps = column.getSortByToggleProps();
                  const sortColumn = sortProps.onClick;
                  const headerProps = column.getHeaderProps(sortProps);

                  return (
                    <Th
                      {...headerProps}
                      key={headerProps.key}
                      color={column.isSorted ? 'gray.700' : 'gray.600'}
                      minWidth={`${column.minWidth}px`}
                      maxWidth={`${column.maxWidth}px`}
                      width={column.width}
                      _hover={
                        column.disableSortBy
                          ? {}
                          : {
                              textColor: 'gray.800',
                            }
                      }
                      onClick={sortColumn}
                    >
                      <Box display="flex" flexDirection="row" alignItems="center">
                        <Box flex="1">{column.render('Header')}</Box>
                        {column.canSort && (
                          <Box>
                            {column.isSorted ? (
                              column.isSortedDesc ? (
                                <ChevronDownIcon display="inherit" />
                              ) : (
                                <ChevronUpIcon display="inherit" />
                              )
                            ) : (
                              <ChevronDownIcon visibility="hidden" />
                            )}
                          </Box>
                        )}
                      </Box>
                    </Th>
                  );
                })}
              </Tr>
            );
          })}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            const rowProps = row.getRowProps();

            const isDisabled = isSubscriptionsEnabled
              ? !isWaveSubscription(row.original.waveSubscription)
              : false;

            return (
              <Tr
                {...rowProps}
                key={rowProps.key}
                {...(isDisabled ? { background: 'gray.50' } : { background: 'blue.' })}
              >
                {row.cells.map((cell) => {
                  const cellProps = cell.getCellProps();
                  return (
                    <Td
                      {...cellProps}
                      key={cellProps.key}
                      {...(isDisabled ? { opacity: 0.5 } : {})}
                      minWidth={`${cell.column.minWidth}px`}
                      maxWidth={`${cell.column.maxWidth}px`}
                      width={cell.column.width}
                    >
                      {cell.render('Cell')}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      <Box display="flex">
        <Box flex="1">
          <UserCustomersTablePageSizeSelector table={table} />
        </Box>
        <UserCustomersTablePagination table={table} />
      </Box>
    </VStack>
  );
}

UserCustomersTable.graphql = {
  fragments: {
    UserCustomersTableTableCustomer: gql`
      fragment UserCustomersTable_customer on PartialCustomer {
        id
        name
        waveSubscription {
          id
        }
      }
    `,
  },
};
